import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ClientMessage,
  PrivateState,
  ServerLobbyUpdate,
  ServerMessage,
  ServerPhaseChanged,
  ServerRoleReveal,
  ServerSessionReplaced,
  ServerError,
  VoteTotal,
  PlayerPublic,
} from '@shum/shared';
import { websocketUrl } from './runtime';
import { useConnectionWatchdog } from './connectionWatchdog';

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface RoomState {
  status: ConnectionStatus;
  lobby: ServerLobbyUpdate | null;
  phase: ServerPhaseChanged | null;
  privateState: PrivateState | null;
  roleReveal: ServerRoleReveal | null;
  voteTotals: VoteTotal[];
  livingPlayers: { playerId: string; name: string }[];
  nightResult: { killed: { name: string } | null } | null;
  elimination: { eliminated: { name: string } | null } | null;
  audio: { trackId: string; mode: 'narration' | 'background'; text?: string } | null;
  error: ServerError | null;
  replaced: ServerSessionReplaced | null;
  hello: { roomId: string } | null;
  selectedPlayerId: string | null;
}

const INITIAL: RoomState = {
  status: 'connecting',
  lobby: null,
  phase: null,
  privateState: null,
  roleReveal: null,
  voteTotals: [],
  livingPlayers: [],
  nightResult: null,
  elimination: null,
  audio: null,
  error: null,
  replaced: null,
  hello: null,
  selectedPlayerId: null,
};

export function getDeviceSessionId(): string {
  const overridden = getDeviceSessionIdFromUrl();
  if (overridden) return overridden;

  try {
    const key = 'shum-device-session';
    let id = localStorage.getItem(key);
    if (!id) {
      id = `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return `dev_${Math.random().toString(36).slice(2)}`;
  }
}

function getDeviceSessionIdFromUrl(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('deviceSessionId');
    if (id && /^[A-Za-z0-9_-]{4,80}$/.test(id)) return id;
  } catch {
    // Ignore malformed URLs or non-browser test environments.
  }
  return null;
}

export function useRoomSocket(code: string | undefined) {
  const [state, setState] = useState<RoomState>(INITIAL);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number>(0);
  const closedByUs = useRef(false);
  const connectRef = useRef<(() => void) | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const lastMessageAtRef = useRef<number>(Date.now());
  const selectedPlayerIdRef = useRef<string | null>(null);
  const identityConfirmedRef = useRef(false);
  const awaitingSnapshotRef = useRef(false);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!code) return;
    closedByUs.current = false;
    setState((s) => ({ ...INITIAL, selectedPlayerId: s.selectedPlayerId }));

    let alive = true;

    const connect = () => {
      const current = wsRef.current;
      if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

      const url = websocketUrl(`/ws/${code}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      const selectedPlayerId = selectedPlayerIdRef.current;
      identityConfirmedRef.current = !selectedPlayerId;
      awaitingSnapshotRef.current = Boolean(selectedPlayerId);
      lastMessageAtRef.current = Date.now();
      setState((s) => ({ ...s, status: 'connecting' }));

      ws.onopen = () => {
        if (!alive || wsRef.current !== ws) return;
        reconnectRef.current = 0;
        lastMessageAtRef.current = Date.now();
        setState((s) => ({ ...s, status: 'connecting', error: null }));
        if (selectedPlayerId) {
          ws.send(JSON.stringify({
            type: 'player.select',
            playerId: selectedPlayerId,
            deviceSessionId: getDeviceSessionId(),
          } satisfies ClientMessage));
        }
      };

      ws.onmessage = (event) => {
        if (!alive || wsRef.current !== ws) return;
        lastMessageAtRef.current = Date.now();
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }
        applyMessage(msg);
      };

      ws.onclose = () => {
        if (!alive || closedByUs.current || wsRef.current !== ws) return;
        wsRef.current = null;
        setState((s) => ({ ...s, status: 'closed' }));
        reconnectRef.current += 1;
        const delay = Math.min(8000, 800 * reconnectRef.current);
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          if (alive && !closedByUs.current) connect();
        }, delay);
      };

      ws.onerror = () => {
        // onclose will follow and schedule a reconnect
      };
    };

    const applyMessage = (msg: ServerMessage) => {
      setState((prev) => {
        switch (msg.type) {
          case 'hello':
            return {
              ...prev,
              hello: { roomId: msg.roomId },
              status: awaitingSnapshotRef.current ? 'connecting' : 'open',
            };
          case 'room.lobby.update': {
            const synchronized = !awaitingSnapshotRef.current || identityConfirmedRef.current;
            if (synchronized) awaitingSnapshotRef.current = false;
            return { ...prev, lobby: msg, status: synchronized ? 'open' : prev.status };
          }
          case 'game.phase.changed':
            return {
              ...prev,
              phase: msg,
              // clear transient per-phase bits on phase change
              nightResult: msg.phase === 'NIGHT_RESOLUTION' ? prev.nightResult : null,
              elimination: msg.phase === 'DAY_RESOLUTION' ? prev.elimination : null,
            };
          case 'phase.timer':
            return prev;
          case 'player.private_state': {
            const synchronized = !awaitingSnapshotRef.current || identityConfirmedRef.current;
            if (synchronized) awaitingSnapshotRef.current = false;
            return {
              ...prev,
              status: synchronized ? 'open' : prev.status,
              phase: {
                type: 'game.phase.changed',
                phase: msg.state.phase,
                dayNumber: msg.state.dayNumber,
                endsAt: msg.state.endsAt,
              },
              privateState: msg.state,
              lobby: prev.lobby
                ? {
                    ...prev.lobby,
                    players: prev.lobby.players.map((player) =>
                      player.playerId === prev.selectedPlayerId ? { ...player, isAlive: msg.state.isAlive } : player,
                    ),
                  }
                : prev.lobby,
            };
          }
          case 'player.living':
            return { ...prev, livingPlayers: msg.players };
          case 'day.vote.totals':
            return { ...prev, voteTotals: msg.totals };
          case 'role.reveal':
            return { ...prev, roleReveal: msg };
          case 'audio.play':
            return { ...prev, audio: { trackId: msg.trackId, mode: msg.mode, text: msg.text } };
          case 'night.result':
            return { ...prev, nightResult: { killed: msg.killed } };
          case 'day.elimination':
            return { ...prev, elimination: { eliminated: msg.eliminated } };
          case 'session_replaced':
            selectedPlayerIdRef.current = null;
            identityConfirmedRef.current = true;
            awaitingSnapshotRef.current = false;
            return { ...prev, status: 'open', replaced: msg, selectedPlayerId: null, privateState: null };
          case 'reconnect':
            selectedPlayerIdRef.current = msg.playerId;
            identityConfirmedRef.current = true;
            return { ...prev, selectedPlayerId: msg.playerId, replaced: null, error: null };
          case 'error':
            if (awaitingSnapshotRef.current && msg.code === 'PLAYER_NOT_FOUND') {
              selectedPlayerIdRef.current = null;
              identityConfirmedRef.current = true;
              awaitingSnapshotRef.current = false;
              return {
                ...prev,
                status: 'open',
                selectedPlayerId: null,
                privateState: null,
                error: msg,
              };
            }
            return { ...prev, error: msg };
          case 'pong':
            return prev;
          default:
            return prev;
        }
      });
    };

    connectRef.current = connect;
    const connectTimer = window.setTimeout(connect, 0);

    return () => {
      alive = false;
      closedByUs.current = true;
      connectRef.current = null;
      window.clearTimeout(connectTimer);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [code]);

  // Force a fresh connection with the same parameters (manual button / watchdog).
  const reconnect = useCallback(() => {
    if (closedByUs.current || !connectRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    const ws = wsRef.current;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        // Already closing/closed; a new socket replaces it below.
      }
    }
    wsRef.current = null;
    reconnectRef.current = 0;
    identityConfirmedRef.current = !selectedPlayerIdRef.current;
    awaitingSnapshotRef.current = Boolean(selectedPlayerIdRef.current);
    setState((s) => ({ ...s, status: 'connecting' }));
    connectRef.current();
  }, []);

  useConnectionWatchdog({ wsRef, stoppedRef: closedByUs, lastMessageAtRef, reconnect });

  const selectPlayer = useCallback(
    (playerId: string) => {
      const ok = send({ type: 'player.select', playerId, deviceSessionId: getDeviceSessionId() });
      if (ok) {
        selectedPlayerIdRef.current = playerId;
        identityConfirmedRef.current = false;
        awaitingSnapshotRef.current = true;
        setState((s) => ({ ...s, status: 'connecting', selectedPlayerId: playerId }));
      }
      return ok;
    },
    [send],
  );

  const reclaim = useCallback(() => {
    return send({ type: 'player.reclaim' });
  }, [send]);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null, replaced: null })), []);

  return useMemo(
    () => ({ state, send, selectPlayer, reclaim, clearError, reconnect }),
    [state, send, selectPlayer, reclaim, clearError, reconnect],
  );
}

export type LobbyPlayers = PlayerPublic[];
